import { FC, useState } from 'react';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import { IInventory } from '../../models/inventory.model';
import { LinksApi, IGenerateLinkParams, IGenerateLinkResponse, ILinksApiCallError } from '../../apis/links.api';
import { isAbortError } from '@monorepo/tools/src/lib/tools/ask/guards';
import { IAskError } from '@monorepo/tools/src/lib/tools/ask/ask';
import { Icon } from '@monorepo/base/src/components/icon/icon';
import { ButtonDimensions, PrimaryButton, SecondaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { IModalInjectProps } from '@monorepo/controlled/src/components/modal/modal/modal';
import styles from './generate-link-modal.module.scss';
import routes from '../../routes/index.routes';

interface IGenerateLinkModal extends IModalInjectProps {
	inventory: IInventory;
	accountId: id;
}

export const GenerateLinkModal: FC<Partial<IGenerateLinkModal>> = ({ inventory, accountId, closeModal }) => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [result, setResult] = useState<IGenerateLinkResponse | null>(null);
	const [copied, setCopied] = useState(false);

	/* Match RH Management: HP/DL from inventory row (inventory.deepLink); campaign-level link, no user path input. */
	const hasDeepLink = Boolean(inventory?.deepLink);
	const linkType: 'HP' | 'DL' = hasDeepLink ? 'DL' : 'HP';
	const geoDisplay = inventory?.geoCode || inventory?.geo;

	const handleGenerate = () => {
		if (!inventory || !accountId) {
			return;
		}
		const params: IGenerateLinkParams = {
			accountId,
			campaignId: inventory.id,
			domain: inventory.domain,
			geo: inventory.geoCode || inventory.geo,
			linkType,
		};

		setIsLoading(true);
		setError(null);

		LinksApi.generateLink(params)
			.then(res => {
				const url = res?.trackingUrl?.trim();
				if (!url) {
					setError('Could not generate link. Please try again.');
					setResult(null);
					return;
				}
				setResult({ ...res, trackingUrl: url });
			})
			.catch((err: unknown) => {
				if (isAbortError(err as IAskError)) {
					return;
				}
				const apiErr = err as ILinksApiCallError;
				if (apiErr?.status === 409) {
					setError('A link for this campaign already exists. Check My Links page.');
				} else if (apiErr?.status === 403) {
					setError('Link generation is not enabled for your account. Please contact your Account Manager.');
				} else {
					setError(apiErr?.message || 'An error occurred while generating the link.');
				}
			})
			.finally(() => setIsLoading(false));
	};

	const handleCopy = () => {
		if (!result?.trackingUrl) {
			return;
		}
		navigator.clipboard.writeText(result.trackingUrl).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		});
	};

	if (result) {
		const myLinksHref =
			accountId != null && `${accountId}` !== ''
				? `${routes.myLinks.index()}?accountId=${encodeURIComponent(String(accountId))}`
				: routes.myLinks.index();

		return (
			<div className={`${styles.container} ${styles.containerSuccess}`}>
				<div className={styles.successHeader}>
					<h3 className={styles.titleSuccess}>Your link is ready</h3>
					<p className={styles.successHint}>Copy the URL below and use it in your placements.</p>
				</div>
				{result.isDuplicate && <div className={styles.alert}>This link already existed — showing your existing link.</div>}
				<div className={styles.urlCard}>
					<span className={styles.urlLabel} id="generate-link-url-label">
						Tracking URL
					</span>
					<div className={styles.urlFieldShell}>
						<textarea
							readOnly
							className={styles.urlInput}
							value={result.trackingUrl}
							rows={3}
							spellCheck={false}
							aria-labelledby="generate-link-url-label"
							onFocus={e => e.currentTarget.select()}
						/>
						<button
							type="button"
							className={styles.copyIconBtn}
							onClick={handleCopy}
							title={copied ? 'Copied' : 'Copy to clipboard'}
							aria-label={copied ? 'Copied to clipboard' : 'Copy URL to clipboard'}>
							<Icon isMFP={true} size="22px" color={copied ? '#12b76a' : '#667085'}>
								{copied ? 'check-circle' : 'copy-06'}
							</Icon>
						</button>
					</div>
				</div>
				<div className={styles.footerSuccess}>
					<a href={myLinksHref} className={styles.myLinksLink} onClick={() => closeModal?.()}>
						Go to My Links
					</a>
					<SecondaryButton dimension={ButtonDimensions.Small} onClick={closeModal} style={{ width: 'auto', minWidth: '4.5rem' }}>
						Close
					</SecondaryButton>
				</div>
			</div>
		);
	}

	return (
		<div className={styles.container}>
			<h3 className={styles.title}>Generate Affiliate Link</h3>
			<div className={styles.metaRow} role="group" aria-label="Offer details">
				<div className={styles.metaCell}>
					<span className={styles.metaLabel}>Domain</span>
					<span className={styles.metaValue}>{inventory?.domain ?? '—'}</span>
				</div>
				<div className={styles.metaCell}>
					<span className={styles.metaLabel}>Geo</span>
					<span className={styles.metaValue}>{geoDisplay ?? '—'}</span>
				</div>
				<div className={styles.metaCell}>
					<span className={styles.metaLabel}>DP/HP</span>
					<span className={styles.metaValue}>{linkType}</span>
				</div>
			</div>
			{error && <div className={styles.error}>{error}</div>}
			<div className={styles.footer}>
				<SecondaryButton onClick={closeModal}>Cancel</SecondaryButton>
				<PrimaryButton onClick={handleGenerate} disabled={isLoading}>
					{isLoading ? 'Generating…' : 'Generate'}
				</PrimaryButton>
			</div>
		</div>
	);
};
